import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { toastApiError } from "@/lib/toastApiError";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Menu } from "lucide-react";
import { AdminSidebar } from "@/components/AdminSidebar";
import type { AdminView, Term, AdminSidebarStats } from "@/admin/types";
import { contributorSubmissions } from "@/admin/termContributionFilter";
import { AdminWordMasterPage } from "@/admin/pages/AdminWordMasterPage";
import { AdminContributionsPage } from "@/admin/pages/AdminContributionsPage";
import { useAdminTerms } from "@/admin/hooks/useAdminTerms";
import { AdminReports } from "@/admin/components/AdminReports";
import { AdminUsers } from "@/admin/components/AdminUsers";
import { AdminUserDialog } from "@/admin/components/AdminUserDialog";
import {
  EditDialog,
  DeleteDialog,
  AddDialog,
} from "@/admin/components/AdminDialogs";
import type { LexiconDirectoryUser } from "@/lib/lexiconUsersApi";
import { AdminMyProfile } from "@/admin/components/AdminMyProfile";
import { AdminActivityModule } from "@/admin/components/AdminActivityModule";
import { adminPathFromView, adminViewFromSegment } from "@/admin/adminRoutes";
import { termToMultipartFields } from "@/lib/termsApi";

const Admin = () => {
  const { staffUser, staffRole, isLexiconStaffAdmin, canAccessStaffDashboard, signOutStaff } =
    useAuth();
  const canManageLexiconUsers =
    staffRole === "admin" || isLexiconStaffAdmin();
  const [lexiconUserDialogOpen, setLexiconUserDialogOpen] = useState(false);
  const [lexiconUserDialogMode, setLexiconUserDialogMode] = useState<
    "create" | "edit"
  >("create");
  const [editingLexiconUser, setEditingLexiconUser] =
    useState<LexiconDirectoryUser | null>(null);
  const [lexiconUsersRefreshKey, setLexiconUsersRefreshKey] = useState(0);
  const [lexiconDirectoryUsers, setLexiconDirectoryUsers] = useState<
    LexiconDirectoryUser[]
  >([]);
  const [pendingUserUpsert, setPendingUserUpsert] =
    useState<LexiconDirectoryUser | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = useParams<{ section: string }>();

  const activeView = adminViewFromSegment(section);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);

  const [formData, setFormData] = useState({
    word: "",
    word_gujarati: "",
    word_devanagari: "",
    word_hindi: "",
    meaning_english: "",
    meaning_hindi: "",
    meaning_gujarati: "",
    pronunciation: "",
    example_usage: "",
    status: "pending" as "pending" | "approved" | "rejected",
    admin_notes: "",
    contributor_name: "",
    contributor_email: "",
    audio_url: "",
    image_url: "",
    featured: false,
    is_enabled: true,
    related_term_ids: [] as string[],
  });
  const [pendingAudioFile, setPendingAudioFile] = useState<File | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  /** True when the existing server image should be cleared on save. */
  const [removeImage, setRemoveImage] = useState(false);

  const {
    terms,
    tablePagination,
    tableQuery,
    setTablePage,
    setTablePageSize,
    setTableSearch,
    setTableStatusFilter,
    isLoadingTerms,
    loadProgress,
    stats,
    alphabetCounts,
    recentTermsByMonth,
    reportTerms,
    fetchTerms,
    upsertTermLocal,
    handleDelete: deleteTerm,
    handleEdit: editTerm,
    handleAdd: addTerm,
    canDeleteTerms,
  } = useAdminTerms(canAccessStaffDashboard);

  const wordMasterServerPagination = useMemo(
    () => ({
      totalCount: tablePagination?.count ?? terms.length,
      page: tableQuery.page,
      pageSize: tableQuery.pageSize,
      search: tableQuery.search,
      statusFilter: tableQuery.statusFilter,
      onPageChange: setTablePage,
      onPageSizeChange: setTablePageSize,
      onSearchChange: setTableSearch,
      onStatusFilterChange: setTableStatusFilter,
    }),
    [
      tablePagination?.count,
      terms.length,
      tableQuery,
      setTablePage,
      setTablePageSize,
      setTableSearch,
      setTableStatusFilter,
    ]
  );

  const contributionTerms = useMemo(
    () => contributorSubmissions(reportTerms, staffUser?.id),
    [reportTerms, staffUser?.id]
  );

  const sidebarStats = useMemo<AdminSidebarStats>(
    () => ({
      ...stats,
      pendingContributions: contributionTerms.filter(
        (t: Term) => t.status === "pending"
      ).length,
    }),
    [stats, contributionTerms]
  );

  const goToView = useCallback(
    (view: AdminView) => {
      navigate(adminPathFromView(view));
    },
    [navigate]
  );

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed(c => !c);
  }, []);

  const handleMobileNav = useCallback(
    (view: AdminView) => {
      navigate(adminPathFromView(view));
      setMobileSidebarOpen(false);
    },
    [navigate]
  );

  const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), []);

  if (!activeView) {
    return <Navigate to="/admin/reports" replace />;
  }

  if (activeView === "users" && !canManageLexiconUsers) {
    return <Navigate to="/admin/reports" replace />;
  }

  const openEditDialog = (term: Term) => {
    setSelectedTerm(term);
    setPendingAudioFile(null);
    setPendingImageFile(null);
    setRemoveImage(false);
    setFormData({
      word: term.word,
      word_gujarati: term.word_gujarati || "",
      word_devanagari: term.word_devanagari || "",
      word_hindi: term.word_hindi || "",
      meaning_english: term.meaning_english || "",
      meaning_hindi: term.meaning_hindi || "",
      meaning_gujarati: term.meaning_gujarati || "",
      pronunciation: term.pronunciation || "",
      example_usage: term.example_usage || "",
      status: term.status,
      admin_notes: term.admin_notes || "",
      contributor_name: term.contributor_name || "",
      contributor_email: term.contributor_email || "",
      audio_url: term.audio_url || "",
      image_url: term.image_url || "",
      featured: term.featured,
      is_enabled: term.is_enabled,
      related_term_ids: term.related_term_ids ?? [],
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    if (!selectedTerm) {
      return;
    }
    await editTerm(
      selectedTerm.id,
      {
        word: formData.word,
        word_gujarati: formData.word_gujarati ?? "",
        word_devanagari: formData.word_devanagari ?? "",
        word_hindi: formData.word_hindi ?? "",
        meaning_english: formData.meaning_english ?? "",
        meaning_hindi: formData.meaning_hindi ?? "",
        meaning_gujarati: formData.meaning_gujarati ?? "",
        pronunciation: formData.pronunciation ?? "",
        example_usage: formData.example_usage ?? "",
        status: formData.status,
        contributor_name: formData.contributor_name ?? "",
        contributor_email: formData.contributor_email ?? "",
        is_featured: formData.featured,
        is_enabled: formData.is_enabled,
        admin_notes: formData.admin_notes ?? "",
        related_term_ids: formData.related_term_ids ?? [],
      },
      pendingAudioFile,
      pendingImageFile,
      removeImage && !pendingImageFile
    );
    setPendingAudioFile(null);
    setPendingImageFile(null);
    setRemoveImage(false);
    setEditDialogOpen(false);
  };

  const openDeleteDialog = (term: Term) => {
    if (!canDeleteTerms) {
      toastApiError("You don't have access to delete terms.");
      return;
    }
    setSelectedTerm(term);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedTerm) {
      return;
    }
    await deleteTerm(selectedTerm);
    setDeleteDialogOpen(false);
  };

  const openAddDialog = () => {
    setPendingAudioFile(null);
    setPendingImageFile(null);
    setFormData({
      word: "",
      word_gujarati: "",
      word_devanagari: "",
      word_hindi: "",
      meaning_english: "",
      meaning_hindi: "",
      meaning_gujarati: "",
      pronunciation: "",
      example_usage: "",
      status: "approved",
      admin_notes: "",
      contributor_name: "",
      contributor_email: "",
      audio_url: "",
      image_url: "",
      featured: false,
      is_enabled: true,
      related_term_ids: [],
    });
    setAddDialogOpen(true);
  };

  const handleAdd = async () => {
    await addTerm(
      {
        word: formData.word,
        word_gujarati: formData.word_gujarati ?? "",
        word_devanagari: formData.word_devanagari ?? "",
        word_hindi: formData.word_hindi ?? "",
        meaning_english: formData.meaning_english ?? "",
        meaning_hindi: formData.meaning_hindi ?? "",
        meaning_gujarati: formData.meaning_gujarati ?? "",
        pronunciation: formData.pronunciation ?? "",
        example_usage: formData.example_usage ?? "",
        status: formData.status,
        contributor_name: formData.contributor_name ?? "",
        contributor_email: formData.contributor_email ?? "",
        is_featured: formData.featured,
        is_enabled: formData.is_enabled,
        related_term_ids: formData.related_term_ids ?? [],
        ...(formData.admin_notes?.trim()
          ? { admin_notes: formData.admin_notes }
          : {}),
      },
      pendingAudioFile,
      pendingImageFile
    );
    setPendingAudioFile(null);
    setPendingImageFile(null);
    setAddDialogOpen(false);
  };

  const handleStatusChange = async (
    term: Term,
    newStatus: "pending" | "approved" | "rejected"
  ) => {
    if (newStatus === term.status) {
      return;
    }
    await editTerm(term.id, {
      ...termToMultipartFields({ ...term, status: newStatus }),
      admin_notes: term.admin_notes ?? "",
    });
  };

  const handleLogout = async () => {
    await signOutStaff();
    navigate("/jlx-vault-entry");
  };

  const getViewTitle = (view: AdminView) => {
    switch (view) {
      case "reports":
        return "Reports & Dashboard";
      case "activity-module":
        return "Activity Module";
      case "word-master":
        return "Word Master";
      case "contributions":
        return "Contributions";
      case "users":
        return "User Management";
      case "my-profile":
        return "My profile";
    }
  };

  const renderContent = () => {
    switch (activeView) {
      case "reports":
        return (
          <AdminReports
            terms={reportTerms}
            stats={stats}
            alphabetCounts={alphabetCounts}
            recentTermsByMonth={recentTermsByMonth}
            onViewChange={goToView}
          />
        );
      case "activity-module":
        return <AdminActivityModule />;
      case "word-master":
        return (
          <AdminWordMasterPage
            terms={terms}
            isLoading={isLoadingTerms}
            loadProgress={loadProgress}
            serverPagination={wordMasterServerPagination}
            onAdd={openAddDialog}
            onEdit={openEditDialog}
            onDelete={openDeleteDialog}
            canDeleteTerms={canDeleteTerms}
            onStatusChange={handleStatusChange}
            onRefresh={fetchTerms}
            onTermUpdated={upsertTermLocal}
          />
        );
      case "contributions":
        return (
          <AdminContributionsPage
            contributionTerms={contributionTerms}
            isLoading={isLoadingTerms}
            onEdit={openEditDialog}
            onDelete={openDeleteDialog}
            canDeleteTerms={canDeleteTerms}
            onStatusChange={handleStatusChange}
            onRefresh={fetchTerms}
            onTermUpdated={upsertTermLocal}
          />
        );
      case "users":
        return (
          <AdminUsers
            canManageUsers={canManageLexiconUsers}
            listRefreshKey={lexiconUsersRefreshKey}
            onDirectoryLoaded={setLexiconDirectoryUsers}
            upsertUser={pendingUserUpsert}
            onUpsertApplied={() => setPendingUserUpsert(null)}
            onAddUser={() => {
              setEditingLexiconUser(null);
              setLexiconUserDialogMode("create");
              setLexiconUserDialogOpen(true);
            }}
            onEditUser={user => {
              setEditingLexiconUser(user);
              setLexiconUserDialogMode("edit");
              setLexiconUserDialogOpen(true);
            }}
          />
        );
      case "my-profile":
        return <AdminMyProfile />;
    }
  };

  return (
    <div className="bg-background flex h-[100dvh] max-h-[100dvh] overflow-hidden">
      <div className="hidden h-full min-h-0 md:block">
        <AdminSidebar
          activeView={activeView}
          onViewChange={goToView}
          stats={sidebarStats}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
          canManageUsers={canManageLexiconUsers}
        />
      </div>

      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeMobileSidebar}
          />
          <div className="relative z-10">
            <AdminSidebar
              activeView={activeView}
              onViewChange={handleMobileNav}
              stats={sidebarStats}
              collapsed={false}
              onToggleCollapse={closeMobileSidebar}
              canManageUsers={canManageLexiconUsers}
            />
          </div>
        </div>
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header className="border-border/50 z-40 flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-3 border-b bg-white px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 md:hidden"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="font-serif min-w-0 break-words text-base font-semibold leading-snug sm:text-lg">
              {getViewTitle(activeView)}
            </h1>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            <span className="text-muted-foreground hidden text-sm sm:inline">
              {staffUser?.email}
            </span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </header>

        <main
          key={location.pathname}
          className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6 [scrollbar-gutter:stable]"
        >
          {renderContent()}
        </main>
      </div>

      <EditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        formData={formData}
        onFormChange={setFormData}
        pendingAudioFile={pendingAudioFile}
        onPendingAudioFileChange={setPendingAudioFile}
        pendingImageFile={pendingImageFile}
        onPendingImageFileChange={file => {
          setPendingImageFile(file);
          if (file) setRemoveImage(false);
        }}
        markedForImageRemoval={removeImage}
        onRemoveImage={() => {
          setPendingImageFile(null);
          setRemoveImage(true);
        }}
        onUndoRemoveImage={() => setRemoveImage(false)}
        onSave={handleEdit}
        allTerms={reportTerms}
        editingId={selectedTerm?.id}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        wordName={selectedTerm?.word || ""}
        onDelete={handleDelete}
      />
      <AddDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        formData={formData}
        onFormChange={setFormData}
        pendingAudioFile={pendingAudioFile}
        onPendingAudioFileChange={setPendingAudioFile}
        pendingImageFile={pendingImageFile}
        onPendingImageFileChange={setPendingImageFile}
        onAdd={handleAdd}
        allTerms={reportTerms}
      />
      <AdminUserDialog
        mode={lexiconUserDialogMode}
        open={lexiconUserDialogOpen}
        onOpenChange={setLexiconUserDialogOpen}
        user={editingLexiconUser}
        existingUsers={lexiconDirectoryUsers}
        onSaved={savedUser => {
          // Optimistic merge when the API echoes the row; otherwise reload.
          if (savedUser) {
            setPendingUserUpsert(savedUser);
          } else {
            setLexiconUsersRefreshKey(k => k + 1);
          }
        }}
      />
    </div>
  );
};

export default Admin;
